import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import Cart from "../Cart/components/Cart";
import TotalCartTable from "../Cart/components/TotalCartTable";
import Input from "./../../components/FormComponents/Input";
import { yupResolver } from "@hookform/resolvers/yup";
import useCart from "../../hooks/useCart";
import useMyAccount from "../../hooks/useMyAccount";
import { formatCurrency } from "../../util/formatters";
import { useAuth } from "../../hooks/useAuth";
import { createCheckoutSchema } from "../../util/validation";
import { CONTACT_INFO } from "../../util/constants";

const checkoutValidationSchema = createCheckoutSchema();

const Checkout = () => {
  const {
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(checkoutValidationSchema),
  });

  const {
    finalizeCart,
    totalValue,
    paymentMethods,
    setPaymentMethod,
    paymentMethod,
    isShoppingCenter,
    selectedTargetCompanyId,
    setSelectedTargetCompanyId,
  } = useCart();

  const { userInfo } = useAuth();
  const buyingLimitTotal = userInfo?.user_buying_limit ?? 0;
  const buyingLimitAvailable = userInfo?.user_buying_limit_available ?? 0;
  const buyingLimitUsedLast30Days =
    userInfo?.user_buying_limit_used_last_30_days ?? 0;
  const buyingLimitSummary = `Total: ${formatCurrency(
    buyingLimitTotal
  )} | Disponivel: ${formatCurrency(
    buyingLimitAvailable
  )} | Usado (30 dias): ${formatCurrency(buyingLimitUsedLast30Days)}`;

  // Mantém o estado do seletor sincronizado com o contexto (evita precisar "clicar 2x"
  // caso o componente remonte/atualize enquanto o contexto ainda está propagando).
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentMethod || null
  );

  const normalizePaymentMethodId = (obj) => {
    if (!obj) return null;
    const id =
      obj.id ??
      obj.payment_method_id ??
      obj.paymentMethodId ??
      obj.id_payment_method ??
      null;
    return id === null || id === undefined ? null : String(id);
  };

  useEffect(() => {
    setSelectedPaymentMethod(paymentMethod || null);
  }, [paymentMethod]);

  // Ao voltar pra tela, o contexto pode manter um método selecionado antigo enquanto
  // a lista `paymentMethods` é recarregada. Se o id selecionado não existir mais,
  // limpamos a seleção para evitar erro ao finalizar.
  useEffect(() => {
    if (!selectedPaymentMethod) return;
    if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) return;
    const selectedId = normalizePaymentMethodId(selectedPaymentMethod);
    const stillValid = paymentMethods.some(
      (m) => normalizePaymentMethodId(m) === selectedId
    );
    if (!stillValid) {
      setSelectedPaymentMethod(null);
      setPaymentMethod(null);
    }
  }, [paymentMethods, selectedPaymentMethod, setPaymentMethod]);

  // Se preferir não “reusar” método selecionado ao sair/voltar, descomente:
  // useEffect(() => () => setPaymentMethod(null), [setPaymentMethod]);

  const finalizeCartHandler = async () => {
    if (isPurchaseDisabled() || !selectedPaymentMethod) return;
    return finalizeCart(selectedPaymentMethod);
  };

  const { paymentDelivery, orderTargetCompanies } = useMyAccount();

  const selectedTargetDelivery = useMemo(() => {
    if (!isShoppingCenter || !Array.isArray(orderTargetCompanies)) return null;
    const row = orderTargetCompanies.find(
      (c) => Number(c.company_id) === Number(selectedTargetCompanyId)
    );
    return row?.delivery ?? null;
  }, [isShoppingCenter, orderTargetCompanies, selectedTargetCompanyId]);

  const displayCompanyName =
    selectedTargetDelivery?.fantasy_name ?? paymentDelivery?.fantasy_name;
  const displayAddress =
    selectedTargetDelivery?.address ?? paymentDelivery?.address;
  const displayComplement =
    selectedTargetDelivery?.complement ?? paymentDelivery?.complement;
  const displayCity = selectedTargetDelivery?.city ?? paymentDelivery?.city;
  const displayState = selectedTargetDelivery?.state ?? paymentDelivery?.state;
  const displayZip =
    selectedTargetDelivery?.zip_code ??
    selectedTargetDelivery?.zip ??
    paymentDelivery?.zip;

  const isPurchaseDisabled = () => {
    return totalValue() > buyingLimitAvailable;
  };

  const selectPaymentMethod = (paymentMethodObj) => {
    setSelectedPaymentMethod(paymentMethodObj);
    // Mantém o contexto atualizado para outros consumidores/validações.
    setPaymentMethod(paymentMethodObj);
  };

  return (
    <div className="container">
      <div className="ltn__checkout-single-content mt-50">
        <h4 className="title-2" style={{ color: "#000" }}>Informações do responsável da compra</h4>
        <div className="ltn__checkout-single-content-info my-5">
          {isShoppingCenter && orderTargetCompanies?.length > 0 && (
            <div className="mb-4">
              <h6>Empresa destino do pedido</h6>
              <p className="text-muted small mb-2">
                O valor do carrinho e a entrega são calculados conforme a empresa
                selecionada.
              </p>
              <select
                className="form-select"
                aria-label="Empresa destino do pedido"
                value={
                  selectedTargetCompanyId != null
                    ? String(selectedTargetCompanyId)
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  const n = parseInt(v, 10);
                  setSelectedTargetCompanyId(Number.isNaN(n) ? null : n);
                }}
              >
                {orderTargetCompanies.map((c) => (
                  <option key={String(c.company_id)} value={String(c.company_id)}>
                    {c.fantasy_name ||
                      c.delivery?.fantasy_name ||
                      `Empresa #${c.company_id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <h6>Informações pessoais</h6>
          <div className="row">
            <div className="col-md-6">
              <Input
                register={register}
                className={"input-item input-item-name mbn ltn__custom-icon"}
                type="text"
                fieldName="firstName"
                placeholder={paymentDelivery?.master_user_name}
                errors={errors}
                disabled
              />
            </div>
            <div className="col-md-6">
              <Input
                register={register}
                className={
                  "input-item input-item-email ltn__custom-icon mb-30"
                }
                type="text"
                fieldName="email"
                placeholder={paymentDelivery?.corporate_email}
                errors={errors}
                disabled
              />
            </div>
            <div className="col-md-6">
              <Input
                register={register}
                className="input-item input-item-phone ltn__custom-icon mb-30"
                type="text"
                fieldName="phoneNumber"
                errors={errors}
                placeholder={paymentDelivery?.phone}
                disabled
              />
            </div>
            <div className="col-md-6">
              <Input
                register={register}
                className="input-item input-item-website ltn__custom-icon mb-30"
                type="text"
                fieldName="companyName"
                placeholder={displayCompanyName}
                errors={errors}
                disabled
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h6>Endereço</h6>
              <div className="row">
                <div className="col-md-6">
                  <Input
                    register={register}
                    className="input-item mb-30"
                    fieldName="address"
                    type="text"
                    placeholder={displayAddress}
                    errors={errors}
                    disabled
                  />
                </div>
                <div className="col-md-6">
                  <Input
                    register={register}
                    className="input-item mb-30"
                    fieldName="addressComplement"
                    type="text"
                    placeholder={displayComplement}
                    errors={errors}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <h6>Cidade</h6>
              <Input
                register={register}
                className="input-item mb-30"
                fieldName="city"
                type="text"
                placeholder={displayCity}
                errors={errors}
                disabled
              />
            </div>
            <div className="col-lg-4 col-md-6 mb-30">
              <h6>Estado</h6>
              <Input
                className="input-item"
                register={register}
                fieldName="state"
                type="text"
                placeholder={displayState}
                errors={errors}
                disabled
              />
            </div>
            <div className="col-lg-4 col-md-6 mb-30">
              <h6>CEP</h6>
              <Input
                className="input-item"
                register={register}
                fieldName="zipCode"
                type="text"
                placeholder={displayZip}
                errors={errors}
                disabled
              />
            </div>
          </div>
        </div>
        <div className="my-5">
          <Cart />
        </div>
      </div>
      <div>
        <h4 className="title-2" style={{ color: "#000" }}>Método de pagamento</h4>
      </div>
      <div
        style={{
          padding: "24px",
          margin: 0,
          border: "1px solid #e5eaee",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {paymentMethods.length === 0 ? (
            <p className="mb-0 text-muted">
              Nenhuma forma de pagamento disponível no momento. Entre em contato com a Terra.
            </p>
          ) : (
            paymentMethods.map((method) => {
              const selectedId = normalizePaymentMethodId(selectedPaymentMethod);
              const methodId = normalizePaymentMethodId(method);
              const isSelected =
                selectedPaymentMethod != null &&
                (selectedId !== null
                  ? selectedId === methodId
                  : selectedPaymentMethod.payment_name === method.payment_name);
              const labelText = [
                method.payment_name,
                method.installments && method.days
                  ? `(${method.installments}x em ${method.days} dias)`
                  : null,
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={
                    method.id ??
                    method.payment_method_id ??
                    method.id_payment_method ??
                    method.payment_name
                  }
                  type="button"
                  // type="button" evita submit acidental se este bloco estiver dentro de um formulário.
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    selectPaymentMethod(method);
                  }}
                  aria-pressed={isSelected}
                  aria-label={labelText}
                  title={labelText}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    margin: 0,
                    padding: "14px 18px",
                    border: `2px solid ${isSelected ? "var(--bs-success, #198754)" : "#e5eaee"}`,
                    borderRadius: "8px",
                    backgroundColor: isSelected ? "rgba(25, 135, 84, 0.06)" : "#fff",
                    cursor: "pointer",
                    transition: "border-color 0.2s, background-color 0.2s",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      marginRight: "10px",
                      borderRadius: "50%",
                      border: `2px solid ${isSelected ? "var(--bs-success, #198754)" : "#999"}`,
                      backgroundColor: isSelected ? "var(--bs-success, #198754)" : "transparent",
                      flexShrink: 0,
                      boxSizing: "border-box",
                    }}
                    aria-hidden
                  />
                  <span
                    style={{
                      fontWeight: isSelected ? 600 : 400,
                      minWidth: 0,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      lineHeight: 1.35,
                    }}
                  >
                    {labelText}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <p className="mt-4 mb-0 text-center text-muted" style={{ fontSize: "0.9rem" }}>
          Caso queira modificar o método de pagamento, entre em contato com a
          Terra pelo e-mail {CONTACT_INFO.EMAIL.ADMIN} ou pelo número {CONTACT_INFO.PHONE.SAC} (SAC)
        </p>
      </div>
      <div className="row justify-content-end mb-4">
        <div className="shoping-cart-total mt-50 justify-content-end">
          <TotalCartTable showCashbackMessage />
          <div className="btn-wrapper text-right go-top">
            <button
              onClick={finalizeCartHandler}
              title="Finalizar compra"
              disabled={isPurchaseDisabled() || !selectedPaymentMethod || paymentMethods.length === 0}
              className={`${isPurchaseDisabled()
                ? "theme-btn-1-opposite btn w-100"
                : "theme-btn-1 btn btn-effect-1 w-100"
                }`}
            >
              Finalizar compra
            </button>
          </div>
          <div className="mini-cart-sub-total">
            <strong style={{ fontSize: "13px", lineHeight: 1.4 }}>
              Limites: <span>{buyingLimitSummary}</span>
            </strong>
          </div>
          {isPurchaseDisabled() && (
            <div>
              <strong>
                <span>
                  Valor total do carrinho ultrapassou limite de compra!
                </span>
              </strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Checkout;
